import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { FaChartLine, FaCoins, FaExchangeAlt, FaShieldAlt, FaTwitter, FaTelegram, FaCopy, FaCheckCircle, FaFileContract, FaChartBar, FaArrowsAltH, FaChevronDown, FaBolt } from 'react-icons/fa';
import { motion } from 'framer-motion';
import './Layout.css';

const Layout = ({ children }) => {
  const location = useLocation();
  const [copied, setCopied] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const CONTRACT_ADDRESS = process.env.REACT_APP_VELAI_TOKEN || '0x1c7c53aa86B49A28C627B6450091998e447A42f9';

  const copyToClipboard = () => {
    navigator.clipboard.writeText(CONTRACT_ADDRESS);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/signal', label: 'Signal' },
    { path: '/swap', label: 'Swap' },
    { path: '/bridge', label: 'Bridge' },
  ];

  const velaiDropdownItems = [
    { path: '/staking', label: 'Staking' },
    { path: '/dashboard', label: 'Dashboard' },
  ];

  return (
    <div className='layout'>
      <header className='header-minimal'>
        <div className='header-inner'>
            <Link to='/' className='logo-minimal'>
              <img src='/logo.png' alt='Velvet AI' className='logo-img' />
              <span className='logo-name'>Velvet AI</span>
            </Link>

            <nav className='nav-minimal'>
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                >
                  {item.label}
                </Link>
              ))}

              {/* $VELAI Dropdown */}
              <div className='nav-dropdown-minimal' ref={dropdownRef}>
                <button
                  className={`nav-link dropdown-btn ${
                    velaiDropdownItems.some(item => location.pathname === item.path) ? 'active' : ''
                  }`}
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  $VELAI
                  <FaChevronDown className={`chevron ${dropdownOpen ? 'open' : ''}`} />
                </button>

                {dropdownOpen && (
                  <motion.div
                    className='dropdown-minimal'
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.15 }}
                  >
                    {velaiDropdownItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`dropdown-link ${location.pathname === item.path ? 'active' : ''}`}
                        onClick={() => setDropdownOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </div>
            </nav>

            <div className='header-right'>
              <ConnectButton />
            </div>
        </div>
      </header>

      <main className='main-content'>
        <div className='container'>
          <motion.div key={location.pathname} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
            {children}
          </motion.div>
        </div>
      </main>

      <footer className='footer'>
        <div className='container'>
          <div className='footer-top'>
            <div className='footer-brand'>
              <div className='footer-logo'>
                <img src='/logo.png' alt='Velvet AI' className='footer-logo-img' />
                <span className='footer-logo-text'>Velvet AI</span>
              </div>
              <p className='footer-tagline'>The Ultimate DeFi Hub: Bridge, Trade, and Earn</p>
              <p className='footer-description'>Cheapest USDC bridging • DeFi vaults • Swaps • AI-powered trading bot • 100% revenue sharing to VELAI holders</p>
            </div>

            <div className='footer-links'>
              <div className='footer-column'>
                <h5 className='footer-heading'>Platform</h5>
                <Link to='/signal' className='footer-link'>
                  <FaBolt className='footer-link-icon' />
                  Signal
                </Link>
                <Link to='/vaults' className='footer-link'>
                  <FaShieldAlt className='footer-link-icon' />
                  Vaults
                </Link>
                <Link to='/swap' className='footer-link'>
                  <FaExchangeAlt className='footer-link-icon' />
                  Swap
                </Link>
                <Link to='/bridge' className='footer-link'>
                  <FaArrowsAltH className='footer-link-icon' />
                  Bridge
                </Link>
                <Link to='/staking' className='footer-link'>
                  <FaCoins className='footer-link-icon' />
                  Staking
                </Link>
                <Link to='/dashboard' className='footer-link'>
                  <FaChartLine className='footer-link-icon' />
                  Dashboard
                </Link>
              </div>

              <div className='footer-column'>
                <h5 className='footer-heading'>Resources</h5>
                <a href='https://dexscreener.com/avalanche/0x1c7c53aa86B49A28C627B6450091998e447A42f9' target='_blank' rel='noopener noreferrer' className='footer-link'>
                  <FaChartBar className='footer-link-icon' />
                  Chart
                </a>
                <a href='https://snowtrace.io/token/0x1c7c53aa86B49A28C627B6450091998e447A42f9' target='_blank' rel='noopener noreferrer' className='footer-link'>
                  <FaFileContract className='footer-link-icon' />
                  Contract
                </a>
              </div>

              <div className='footer-column'>
                <h5 className='footer-heading'>Community</h5>
                <div className='footer-socials'>
                  <a href='https://x.com/0xVelvetai' target='_blank' rel='noopener noreferrer' className='footer-social-link' aria-label='Twitter'>
                    <FaTwitter />
                  </a>
                  <a href='https://t.me/velvet_ai_dao' target='_blank' rel='noopener noreferrer' className='footer-social-link' aria-label='Telegram'>
                    <FaTelegram />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className='footer-contract'>
            <div className='contract-box' onClick={copyToClipboard}>
              <span className='contract-label'>VELAI Token Contract:</span>
              <code className='contract-address'>{CONTRACT_ADDRESS}</code>
              <button className='copy-button' aria-label='Copy contract address'>
                {copied ? <FaCheckCircle className='copy-icon success' /> : <FaCopy className='copy-icon' />}
              </button>
            </div>
            {copied && <span className='copy-tooltip'>Copied to clipboard!</span>}
          </div>

          <div className='footer-bottom'>
            <p className='copyright'>© 2025 Velvet AI DAO. All rights reserved.</p>
            <p className='disclaimer'>Trading involves risk. DYOR.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
